"use client";

import * as React from "react";

interface NotificationBannerClientProps {
  children: (props: {
    isVisible: boolean;
    bannerRef: React.RefObject<HTMLDivElement | null>;
    handleClose: () => void;
  }) => React.ReactNode;
  storageKey: string;
}

export const NotificationBannerClient: React.FC<
  NotificationBannerClientProps
> = ({ children, storageKey }) => {
  // Initialize state based on localStorage (inline script already handled this)
  const [isVisible, setIsVisible] = React.useState(() => {
    if (typeof window === "undefined") return true;
    return !localStorage.getItem(storageKey);
  });

  const bannerRef = React.useRef<HTMLDivElement>(null);

  // Update height on window resize
  React.useLayoutEffect(() => {
    if (!bannerRef.current) return;

    const updateHeight = () => {
      if (bannerRef.current) {
        const height = isVisible ? bannerRef.current.offsetHeight : 0;
        document.documentElement.style.setProperty(
          "--notification-banner-height",
          `${height}px`,
        );
      }
    };

    // Set initial height and update on resize
    updateHeight();
    window.addEventListener("resize", updateHeight);

    return () => {
      window.removeEventListener("resize", updateHeight);
    };
  }, [isVisible]);

  const handleClose = React.useCallback(() => {
    setIsVisible(false);

    // Drop stale dismissal keys for this banner before writing the current one:
    // the legacy `<base>` (pre-hash format) + older content-hash versions
    // `<base>-<hash>`, so localStorage doesn't accumulate one entry per text edit.
    // `base` = storageKey without the trailing `-<hash>` (hash has no hyphen).
    const base = storageKey.slice(0, storageKey.lastIndexOf("-"));
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k !== storageKey && (k === base || k.startsWith(`${base}-`))) {
        stale.push(k);
      }
    }
    stale.forEach((k) => localStorage.removeItem(k));
    localStorage.setItem(storageKey, "true");

    // Animate height to 0 when closed
    document.documentElement.style.setProperty(
      "--notification-banner-height",
      "0px",
    );
  }, [storageKey]);

  return <>{children({ isVisible, bannerRef, handleClose })}</>;
};
